/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/TriageBand.tsx
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-BAND-001
 * PURPOSE: Render one decision locus and the semantic context surface.
 * ROLE IN ARCHITECTURE: Shared reader-header frame leaf above the message body.
 * MAIN EXPORTS: ReaderTriageBand.
 * NON-RESPONSIBILITIES: Decision transports and the Review Queue live in the
 *   adapter's DecisionActions cluster (the `decisionContent` slot).
 * NOTES FOR FUTURE AI: Never render a second confirm/reject cluster outside
 *   the decisionContent slot (D269-12).
 *
 *   The compressed line and the lanes are ALTERNATIVES, never both (change
 *   #294). Showing the lanes plus a summary of the lanes cost 32px to restate
 *   what was already on screen, and left the operator staring at a mostly
 *   empty row. Open → lanes + a 12px collapse handle. Closed → the compressed
 *   line, which carries the LIVE placements control rather than a text copy of
 *   it, so "1 placement" is never a dead word.
 */
import { ChevronUp, X } from 'lucide-react'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '../../../_internal/lib/utils'
import type { ReaderHeaderViewModel } from '../model/types'
import type { ReaderHeaderDensity } from '../responsive/density'
import { COLLAPSE_HANDLE_H, MOTION_MS, SUMMARY_STRIP_H } from '../tokens'
import { ReaderDecisionLine } from './DecisionLine'
import { ReaderTriageSummary, TriageChevron } from './TriageSummary'
import { useTriageExpansion } from './useTriageExpansion'

export interface ReaderTriageBandProps {
  model: ReaderHeaderViewModel
  density: ReaderHeaderDensity
  /** False while `density` is still the pre-measurement guess (see density.ts). */
  densityMeasured: boolean
  decisionContent?: ReactNode
  summaryContent?: ReactNode
  expandedContent: ReactNode
}

/** 28px compressed context strip under a pending decision line. */
function SummaryStrip({
  model,
  compact,
  expanded,
  onToggle,
  placementsControl,
}: {
  model: ReaderHeaderViewModel
  compact: boolean
  expanded: boolean
  onToggle: () => void
  /** Live placements chip; it REPLACES the "N placements" text token. */
  placementsControl?: ReactNode
}) {
  const tokens = model.context.tokens
    // With the real control present the text copy is a duplicate — and it was
    // the duplicate the operator kept trying to click (change #294).
    .filter((token) => !placementsControl || !/placements?$/.test(token))
    .slice(0, compact ? 2 : 4)
  const hasText = tokens.length > 0
  return (
    <div
      className="group flex items-center gap-1.5 overflow-hidden px-3"
      style={{ height: SUMMARY_STRIP_H }}
      data-testid="reader-summary-strip"
    >
      {/* The token run opens the lanes, same as the chevron. On a phone the row
          is too tight to guarantee the chevron survives, and these tokens are
          the only other thing in reach — as inert text they read as a broken
          control (2026-08-14). */}
      {(hasText || !placementsControl) && (
        <button
          type="button"
          onClick={onToggle}
          data-testid="reader-summary-strip-open"
          aria-expanded={expanded}
          aria-controls="reader-expanded-context"
          title={expanded ? 'Collapse context' : 'Show full context'}
          className={cn(
            'flex h-full min-w-0 shrink items-center gap-1.5 overflow-hidden rounded-md px-1',
            'hover:bg-accent focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-primary/50 focus-visible:ring-offset-1',
          )}
        >
          {hasText ? (
            tokens.map((token) => (
              <span
                key={token}
                className="max-w-[180px] shrink truncate rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                title={token}
              >
                {token}
              </span>
            ))
          ) : (
            <span className="whitespace-nowrap text-[10px] text-muted-foreground">
              No context assigned
            </span>
          )}
        </button>
      )}
      {placementsControl && (
        // Shrinkable: at shrink-0 the placement breadcrumb pushed the chevron
        // out past this row's overflow-hidden edge on a phone reader.
        <span className="flex min-w-0 shrink items-center overflow-hidden">
          {placementsControl}
        </span>
      )}
      <span className="ml-auto flex shrink-0 items-center">
        <TriageChevron expanded={expanded} onToggle={onToggle} />
      </span>
    </div>
  )
}

/**
 * 12px hairline handle that closes the open context.
 *
 * The collapse affordance needs a home once the compressed line stops
 * rendering underneath the lanes. A full control row would spend 32px of body
 * budget to hold one chevron, which is exactly the empty space the operator
 * objected to; this reads as the bottom edge of the section it closes.
 */
function CollapseHandle({ onCollapse }: { onCollapse: () => void }) {
  return (
    <button
      type="button"
      onClick={onCollapse}
      style={{ height: COLLAPSE_HANDLE_H }}
      className={cn(
        'relative flex w-full items-center justify-center border-t border-border/50 text-muted-foreground',
        'hover:bg-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50',
        "before:absolute before:inset-x-0 before:content-[''] pointer-coarse:before:inset-y-[-10px]",
      )}
      aria-expanded
      aria-controls="reader-expanded-context"
      aria-label="Hide email context"
      title="Hide context"
      data-testid="reader-context-collapse-handle"
    >
      <ChevronUp className="h-3 w-3" />
    </button>
  )
}

export function ReaderTriageBand({
  model,
  density,
  densityMeasured,
  decisionContent,
  summaryContent,
  expandedContent,
}: ReaderTriageBandProps) {
  const compact = density === 'compact'
  // Open by default everywhere the panel is inline. On compact the panel is a
  // bottom sheet ON TOP of the message, so defaulting it open would cover the
  // thing the operator opened. An explicit choice always wins over both.
  //
  // The default waits for a real measurement: acting on the pre-measurement
  // 'standard' guess mounted the lanes on a narrow reader and tore them down
  // one commit later. A stored choice needs no measurement and applies at once.
  const { expanded, setExpanded, toggle } = useTriageExpansion({
    defaultExpanded: densityMeasured && !compact,
  })
  const pending = model.currentDecision

  // WORK ITEM 9: expand 160ms / collapse 120ms via MOTION_MS, delayed-unmount
  // plus a grid-rows 0fr/1fr transition (precedent: packages/ui/src/
  // components/email/EmailRow.tsx's row-action-bar). The expanded subtree
  // (workflow row, watcher pickers, placement manager) is expensive, so it
  // stays fully unmounted at rest; only the closing edge keeps it mounted
  // for one collapse-duration tail so the shrink has something to animate.
  const [panelMounted, setPanelMounted] = useState(expanded)
  const [panelOpen, setPanelOpen] = useState(expanded)
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The resting state must not animate in. This is a layout effect so the
  // commit that follows the first density measurement paints WITH the lanes
  // already open, instead of one frame of headerless-context then a slide.
  const hasOpened = useRef(expanded)
  useLayoutEffect(() => {
    if (expanded) {
      if (collapseTimer.current) {
        clearTimeout(collapseTimer.current)
        collapseTimer.current = null
      }
      setPanelMounted(true)
      if (!hasOpened.current) {
        hasOpened.current = true
        setPanelOpen(true)
        return
      }
      // A real operator toggle: mount at grid-rows-[0fr] first, flip to [1fr]
      // next frame so the browser has a starting frame to transition FROM (no
      // transition plays if the mount and the open state land in one commit).
      const raf = requestAnimationFrame(() => setPanelOpen(true))
      return () => cancelAnimationFrame(raf)
    }
    setPanelOpen(false)
    collapseTimer.current = setTimeout(() => {
      setPanelMounted(false)
    }, MOTION_MS.collapse)
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current)
    }
  }, [expanded])

  const expandedPanel = (
    <div
      className={cn(
        'border-t border-border/50 bg-background',
        compact
          ? 'fixed inset-x-0 bottom-0 z-50 max-h-[82vh] overflow-y-auto rounded-t-2xl border shadow-2xl'
          : 'relative',
      )}
      data-testid="reader-context-panel"
    >
      {compact && (
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-3 py-2">
          <span className="text-sm font-semibold">Email context</span>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11"
            aria-label="Close email context"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Breathing room around the lane strip — tight p-2 + nested muted
          boxes read as a cramped spreadsheet (operator 2026-08-05). */}
      <div className="px-3 py-2.5">{expandedContent}</div>
      {/* The sheet already has a titled close button; a second one below the
          fold would be unreachable without scrolling the sheet. */}
      {!compact && <CollapseHandle onCollapse={() => setExpanded(false)} />}
    </div>
  )

  return (
    <section
      className="border-b border-border/50 bg-muted/10"
      aria-label="Email triage"
      data-testid="reader-triage-band"
    >
      {pending && (
        <ReaderDecisionLine decision={pending} controls={decisionContent} />
      )}
      {/* Compressed line ONLY while the lanes are hidden — see the file header.
          It carries the live placements control so the one thing the operator
          most often acts on is never reduced to unclickable text. When the
          lanes are showing, the FiledTo lane owns that same control. */}
      {!expanded &&
        (pending ? (
          <SummaryStrip
            model={model}
            compact={compact}
            expanded={expanded}
            onToggle={toggle}
            placementsControl={summaryContent}
          />
        ) : (
          <ReaderTriageSummary
            context={model.context}
            expanded={expanded}
            onToggle={toggle}
            placementsControl={summaryContent}
          />
        ))}
      {compact && expanded && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/35"
          aria-label="Close email context"
          onClick={() => setExpanded(false)}
        />
      )}
      <div
        id="reader-expanded-context"
        className={cn(
          'grid overflow-hidden transition-[grid-template-rows] ease-out motion-reduce:transition-none',
          panelOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        style={{
          transitionDuration: `${expanded ? MOTION_MS.expand : MOTION_MS.collapse}ms`,
        }}
      >
        <div className="min-h-0 overflow-hidden">
          {panelMounted && expandedPanel}
        </div>
      </div>
    </section>
  )
}
